import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { MessageCircle, Plus, Send, Clock, CheckCircle, AlertCircle, XCircle, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const statusConfig = {
  open: { label: 'Aberto', color: 'bg-blue-500', icon: Clock },
  in_progress: { label: 'Em Andamento', color: 'bg-yellow-500', icon: AlertCircle },
  resolved: { label: 'Resolvido', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Fechado', color: 'bg-gray-500', icon: XCircle }
};

const priorityConfig = {
  low: { label: 'Baixa', color: 'text-gray-400' },
  normal: { label: 'Normal', color: 'text-blue-400' },
  high: { label: 'Alta', color: 'text-orange-400' },
  urgent: { label: 'Urgente', color: 'text-red-400' }
};

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [newTicketForm, setNewTicketForm] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      fetchTicketDetails(selectedTicket);
    }
  }, [selectedTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticketDetails?.messages]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`);
      setTickets(res.data);
    } catch (e) {
      toast.error('Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticketId}`);
      setTicketDetails(res.data);
    } catch (e) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicketForm.subject || !newTicketForm.message) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      setSending(true);
      const res = await axios.post(`${API}/support/tickets`, newTicketForm);
      toast.success('Ticket criado com sucesso!');
      setShowNewTicket(false);
      setNewTicketForm({ subject: '', message: '', priority: 'normal' });
      fetchTickets();
      setSelectedTicket(res.data.id);
    } catch (e) {
      toast.error('Erro ao criar ticket');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      await axios.post(`${API}/support/tickets/${selectedTicket}/messages`, {
        message: newMessage
      });
      setNewMessage('');
      fetchTicketDetails(selectedTicket);
    } catch (e) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Suporte">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#ff6b00] border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Suporte">
      <div className="flex h-[calc(100vh-180px)] gap-4">
        {/* Tickets List */}
        <div className="w-80 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Meus Tickets</h2>
            <Button
              size="sm"
              onClick={() => setShowNewTicket(true)}
              className="bg-[#ff6b00] hover:bg-[#ff8533]"
            >
              <Plus className="w-4 h-4 mr-1" />
              Novo
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-2">
            {tickets.length === 0 ? (
              <Card className="bg-[#13131f] border-white/10 p-6 text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-white/20" />
                <p className="text-white/60">Nenhum ticket ainda</p>
                <p className="text-sm text-white/40">Clique em "Novo" para abrir um ticket</p>
              </Card>
            ) : (
              tickets.map(ticket => {
                const StatusIcon = statusConfig[ticket.status]?.icon || Clock;
                return (
                  <Card
                    key={ticket.id}
                    className={`bg-[#13131f] border-white/10 p-3 cursor-pointer transition-all hover:border-[#ff6b00]/50 ${
                      selectedTicket === ticket.id ? 'border-[#ff6b00]' : ''
                    }`}
                    onClick={() => setSelectedTicket(ticket.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{ticket.subject}</p>
                        <p className="text-xs text-white/40 mt-1">
                          {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={`${statusConfig[ticket.status]?.color} text-white text-xs`}>
                          {statusConfig[ticket.status]?.label}
                        </Badge>
                        <span className={`text-xs ${priorityConfig[ticket.priority]?.color}`}>
                          {priorityConfig[ticket.priority]?.label}
                        </span>
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <Card className="flex-1 bg-[#13131f] border-white/10 flex flex-col">
          {selectedTicket && ticketDetails ? (
            <>
              {/* Header */}
              <CardHeader className="border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">{ticketDetails.ticket.subject}</CardTitle>
                    <p className="text-sm text-white/60">
                      Criado em {new Date(ticketDetails.ticket.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <Badge className={`${statusConfig[ticketDetails.ticket.status]?.color} text-white`}>
                    {statusConfig[ticketDetails.ticket.status]?.label}
                  </Badge>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                {ticketDetails.messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_role === 'super_admin' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        msg.sender_role === 'super_admin'
                          ? 'bg-blue-500/20 text-white'
                          : 'bg-[#ff6b00]/20 text-white'
                      }`}
                    >
                      <p className="text-xs text-white/60 mb-1">
                        {msg.sender_role === 'super_admin' ? '👨‍💼 Suporte' : '👤 Você'} • 
                        {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </CardContent>

              {/* Input */}
              {ticketDetails.ticket.status !== 'closed' && (
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Digite sua mensagem..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="bg-[#ff6b00] hover:bg-[#ff8533]"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-white/40">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p>Selecione um ticket para ver as mensagens</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New Ticket Modal */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="bg-[#13131f] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Ticket de Suporte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-white/60">Assunto</label>
              <Input
                placeholder="Descreva brevemente o problema"
                value={newTicketForm.subject}
                onChange={(e) => setNewTicketForm({ ...newTicketForm, subject: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-white/60">Prioridade</label>
              <Select
                value={newTicketForm.priority}
                onValueChange={(v) => setNewTicketForm({ ...newTicketForm, priority: v })}
              >
                <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10">
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/60">Mensagem</label>
              <Textarea
                placeholder="Descreva seu problema em detalhes..."
                value={newTicketForm.message}
                onChange={(e) => setNewTicketForm({ ...newTicketForm, message: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white mt-1 min-h-[120px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewTicket(false)}>Cancelar</Button>
            <Button
              onClick={handleCreateTicket}
              disabled={sending}
              className="bg-[#ff6b00] hover:bg-[#ff8533]"
            >
              {sending ? 'Enviando...' : 'Enviar Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Support;
